/**
 * useZoneManifest — in-memory-only manifest hook (WO#4a §13 + §5 fix).
 *
 * Fix Round #1 §5 — CACHE FRESHNESS RULE (ratified by Neo):
 *   The zone manifest is NEVER persisted across page loads. Every fresh
 *   page load performs a network fetch of `/api/v1/sale/zones` and the
 *   result lives in React state ONLY for the lifetime of the tab
 *   session. This eliminates the entire class of stale-hash bugs where
 *   an emitter fix changes `manifest_hash` while `sale_authority` stays
 *   the same — a `sessionStorage` cache would silently serve the pre-fix
 *   manifest until the next authority change.
 *
 *   Trade-off vs ETag/If-None-Match:
 *     * Manifest is ~1.6 KB → cost of an unconditional refetch is
 *       negligible.
 *     * No shared-invariant between client persistence & server hash.
 *     * Simpler failure model: only two states (`loading` → `ready` |
 *       `unavailable`).
 *
 * Invalidation:
 *   * `SaleAuthorityProvider` publishes the current
 *     `(chain_id, sale_contract_address)`. When that pair changes we
 *     drop the in-memory manifest and refetch.
 *   * The synchronous render guard `useOverlayRenderable` still runs
 *     inside the render pass to prevent V3-quote/V2-overlay races
 *     (WO#4a §12 zero-render stale guarantee).
 */
import * as React from "react";

import { fetchZoneManifest } from "../lib/zone-manifest";
import type { ZoneManifest, ZoneManifestState } from "../lib/zone-manifest";
import { useCurrentSaleAuthority } from "./SaleAuthorityProvider";

export function useZoneManifest(): ZoneManifestState & { retry: () => void } {
  const authority = useCurrentSaleAuthority();
  const [state, setState] = React.useState<ZoneManifestState>({ status: "loading" });

  // Serialized request id — the async fetch below checks this ref
  // before applying its result so a stale response can never overwrite
  // a fresher one initiated by a later authority change.
  const reqIdRef = React.useRef(0);
  const failCountRef = React.useRef(0);

  // `silent` keeps the current `unavailable` state during a background
  // retry, so the Grid never flips back to a loading veil.
  const runFetch = React.useCallback((silent = false) => {
    const myId = ++reqIdRef.current;
    if (!silent) setState({ status: "loading" });
    void (async () => {
      try {
        const m = await fetchZoneManifest();
        if (reqIdRef.current !== myId) return;
        failCountRef.current = 0;
        setState({ status: "ready", manifest: m });
      } catch (e) {
        if (reqIdRef.current !== myId) return;
        failCountRef.current += 1;
        const code = (e as { code?: string })?.code || "FETCH_FAILED";
        const message = (e as Error)?.message || "Zone manifest unavailable";
        setState({ status: "unavailable", code, message });
      }
    })();
  }, []);

  // Defect C — bounded automatic recovery: 5s, 10s, 20s, 40s, then every 60s.
  React.useEffect(() => {
    if (state.status !== "unavailable") return;
    const delay = Math.min(ZONE_RETRY_BASE_MS * 2 ** (failCountRef.current - 1), ZONE_RETRY_MAX_MS);
    const id = window.setTimeout(() => runFetch(true), delay);
    return () => window.clearTimeout(id);
  }, [state, runFetch]);

  const retry = React.useCallback(() => runFetch(true), [runFetch]);

  // Effect 1 — bootstrap fetch on mount. Always runs, no persistent
  // cache to short-circuit.
  React.useEffect(() => {
    runFetch();
  }, [runFetch]);

  // Effect 2 — re-fetch whenever the observed authority triad changes.
  // The manifest identity carries its own (chain_id, sale_contract_address,
  // runtime_template_hash, manifest_hash); a change to any of these
  // upstream MUST produce a fresh network read to reveal the new hash.
  React.useEffect(() => {
    if (authority == null) return;
    if (state.status === "ready") {
      const cur = state.manifest.identity;
      if (
        cur.chain_id === authority.chain_id
        && cur.sale_contract_address.toLowerCase() ===
           authority.sale_contract_address.toLowerCase()
      ) {
        return; // authority unchanged since last fetch; nothing to do
      }
    }
    runFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authority?.chain_id, authority?.sale_contract_address, runFetch]);

  return { ...state, retry };
}

export const ZONE_RETRY_BASE_MS = 5_000;
export const ZONE_RETRY_MAX_MS = 60_000;

/** Derive the manifest that is SAFE to render given the current
 * authority. Returns `null` if any invariant breaks — this is the
 * WO#4a §12 zero-render stale-authority guarantee, evaluated inside
 * the render pass with no useEffect on the critical path.
 */
export function useOverlayRenderable(state: ZoneManifestState): ZoneManifest | null {
  const authority = useCurrentSaleAuthority();
  if (state.status !== "ready") return null;
  if (authority == null) return null;
  const id = state.manifest.identity;
  if (id.chain_id !== authority.chain_id) return null;
  if (id.sale_contract_address.toLowerCase() !== authority.sale_contract_address.toLowerCase()) {
    return null;
  }
  return state.manifest;
}
